import type { SlotDto } from '@ffs/shared';
import { useMemo } from 'preact/hooks';
import { zoneOptions } from '../lib/tz';

export type SlotChoice = number | 'next_jit';

export const slotValue = (s: SlotDto): SlotChoice => (s.jit ? 'next_jit' : s.start_at);

/** True when `choice` is still offered in `slots`. */
export const choiceOffered = (slots: SlotDto[], choice: SlotChoice | null): boolean =>
  choice !== null && slots.some((s) => slotValue(s) === choice);

/** The soonest slot. Slots arrive sorted by start time, so that is the first one. */
export const defaultChoice = (slots: SlotDto[]): SlotChoice | null => {
  const first = slots[0];
  return first ? slotValue(first) : null;
};

interface Props {
  slots: SlotDto[] | null;
  loading: boolean;
  error: string | null;
  fieldError?: string;
  tz: string;
  detectedTz: string;
  selected: SlotChoice | null;
  disabled?: boolean;
  heading?: string;
  onSelect: (choice: SlotChoice) => void;
  onTzChange: (tz: string) => void;
}

export function SlotPicker(p: Props) {
  const zones = useMemo(() => zoneOptions(p.detectedTz, p.tz), [p.detectedTz, p.tz]);
  const err = p.fieldError ?? p.error ?? undefined;
  return (
    <fieldset
      class="field fs"
      aria-labelledby="slot-legend"
      aria-describedby={err ? 'slot-err' : undefined}
      aria-invalid={err ? 'true' : undefined}
      disabled={p.disabled}
    >
      <div class="slots-head">
        <span class="label" id="slot-legend">
          {p.heading ?? 'Choose a time'}
        </span>
        <label class="sr" for="tz">
          Time zone
        </label>
        <select
          class="tz"
          id="tz"
          value={p.tz}
          onChange={(e) => p.onTzChange((e.currentTarget as HTMLSelectElement).value)}
        >
          {zones.map((z) => (
            <option key={z.value} value={z.value}>
              {z.label}
            </option>
          ))}
        </select>
      </div>
      {p.slots === null ? (
        <div class="slots" aria-busy="true">
          <div class="skel" />
          <div class="skel" />
          <div class="skel" />
        </div>
      ) : p.slots.length === 0 ? (
        <p class="empty">No upcoming times right now. Please check back soon.</p>
      ) : (
        <div class="slots" role="radiogroup" aria-labelledby="slot-legend" aria-busy={p.loading}>
          {p.slots.map((s, i) => {
            const value = slotValue(s);
            const id = `slot-${s.jit ? 'jit' : s.start_at}`;
            const sel = p.selected === value;
            // The just-in-time slot is "next" only when nothing scheduled starts before it.
            const next = s.jit && i === 0;
            return (
              <label key={id} class={`slot${sel ? ' sel' : ''}`} for={id}>
                <input
                  type="radio"
                  name="slot"
                  id={id}
                  value={String(value)}
                  checked={sel}
                  onChange={() => p.onSelect(value)}
                />
                <span class="dot" aria-hidden="true" />
                <span class="slot-text">{s.label}</span>
                {next ? <span class="badge">Next session</span> : null}
              </label>
            );
          })}
        </div>
      )}
      {err ? (
        <span class="err slot-err" id="slot-err">
          {err}
        </span>
      ) : null}
    </fieldset>
  );
}
